import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getBalanceScorecardFieldsets } from "store/balanceScorecardSlice";
import { getMenus, getTenantIsInternal } from "store/userSlice";
import { Outlet, useLocation, useOutletContext } from "react-router-dom";
import Link from "components/ui/Link";
import { scrollInto } from "helpers/common";
import { getCalculationSettingsSections } from "store/calculationSettingsSlice";
import { useTranslation } from "translation/useTranslation";

export default function Index() {
  const { helps } = useOutletContext();

  const location = useLocation();
  const { pathname } = location;

  const { translate, editableTranslate } = useTranslation();

  const menus = useSelector(getMenus);
  const balanceScorecardFieldsets = useSelector(getBalanceScorecardFieldsets);
  const calculationSettingsSections = useSelector(getCalculationSettingsSections);

  const [activeFieldset, setActiveFieldset] = useState(null);
  const [activeCalculationSection, setActiveCalculationSection] = useState(null);

  const isSystemSetting = useMemo(() => {
    return pathname.startsWith('/system-settings');
  }, [pathname]);

  const subMenus = useMemo(() => {
    const path = isSystemSetting ? '/system-settings' : '/settings';
    if (menus) {
      for (let i = 0; i < menus.length; i++) {
        if (menus[i].path === path) return menus[i].submenus.data;
      }
    }
    return [];
  }, [menus, isSystemSetting]);

  const sections = useMemo(() => {
    const serviceAgreements = ['balance-scorecard', 'calculation', 'budgets', 'interview'];
    const personnels = ['import', 'employee-import-sources', 'user-import', 'actions', 'batch-update', 'login-users', 'roles', 'groups'];

    const obj = {
      'service_agreement': [],
      'personnel': [],
      'others': []
    };

    for (const sub of subMenus) {
      if (serviceAgreements.includes(sub.path?.replace('/settings/', ''))) {
        obj.service_agreement.push(sub);
      } else if (personnels.includes(sub.path?.replace('/settings/', ''))) {
        obj.personnel.push(sub);
      } else {
        obj.others.push(sub);
      }
    }

    return obj;
  }, [subMenus, isSystemSetting]);

  useEffect(() => {
  }, []);

  const handleClickSection = (e, query) => {
    e.preventDefault();
    scrollInto(query);
  };

  return (
    <div className={`row flex-nowrap ${isSystemSetting ? 'settings-row--full' : ''}`}>
      {!isSystemSetting && <div className="col-lg-auto col-md-3 section-col-left d-flex min-height-after-navbar">
        <div className="bg-white w-100 p-4 content-card">
          <ul className="sidebar-nav nav nav-pills flex-column sticky" data-testid="settings-nav">
            {
              sections.service_agreement.length > 0 && <li className="nav-item text-big text-primary font-weight-bold pb-1">{editableTranslate('global.menu.settings.section.service_agreement')}</li>
            }
            {
              sections.service_agreement.map((sub, i) => {
                const subKey = (sub.path || '').split('/').filter(Boolean).pop();
                if (sub.path === '/settings/balance-scorecard') {
                  const isActiveSection = pathname.includes('/settings/balance-scorecard');
                  const isBoxed = pathname.includes('/settings/balance-scorecard/') && balanceScorecardFieldsets.length > 0;
                  return <li key={i} className="nav-item">
                    <Link to={sub.path} className={`nav-link ${isBoxed ? 'has-children' : (isActiveSection ? 'active' : '')}`} data-toggle="collapse" href="#balance-scorecard-submenu-collapse" data-testid={`nav-item-${subKey}`}>
                      <i className={`${sub.icon} pr-2`}></i> {editableTranslate(sub.translation_key, sub?.name)} {isBoxed && <i className="bi bi-chevron-up"></i>}
                    </Link>
                    {
                      balanceScorecardFieldsets.length > 0 && <div className={`collapse ${pathname.includes('/settings/balance-scorecard/') ? 'show' : ''}`} id="balance-scorecard-submenu-collapse">
                        <div>
                          {
                            balanceScorecardFieldsets.map((fieldset, i) => {
                              return <a key={i} data-testid={`fieldset-link-${fieldset.key}`} href="/#" onClick={(e) => handleClickSection(e, `#fieldset-${fieldset.key}`)} className={`${activeFieldset === fieldset.key || (i === 0 && !activeFieldset) ? 'active' : ''} ${fieldset.show ? '' : 'd-none'}`}>{fieldset.label}</a>
                            })
                          }
                        </div>
                      </div>
                    }
                  </li>
                } else if (sub.path === '/settings/calculation') {
                  const isActiveSection = pathname.includes('/settings/calculation');
                  const isBoxed = pathname.includes('/settings/calculation/form') && calculationSettingsSections.length > 0;
                  return <li key={i} className="nav-item">
                    <Link to={sub.path} className={`nav-link ${isBoxed ? 'has-children' : (isActiveSection ? 'active' : '')}`} data-toggle="collapse" href="#calculation-submenu-collapse" data-testid={`nav-item-${subKey}`}>
                      <i className={`${sub.icon} pr-2`}></i> {editableTranslate(sub.translation_key, sub?.name)} {isBoxed && <i className="bi bi-chevron-up"></i>}
                    </Link>
                    {
                      calculationSettingsSections.length > 0 && <div className={`collapse ${pathname.includes('/settings/calculation/form') ? 'show' : ''}`} id="calculation-submenu-collapse">
                        <div>
                          {
                            calculationSettingsSections.map((section, i) => {
                              return <a key={i} data-testid={`section-link-${section}`} href="/#" onClick={(e) => handleClickSection(e, `#${section}-section`)} className={`${activeCalculationSection === section || (i === 0 && !activeCalculationSection) ? 'active' : ''}`}>{editableTranslate(`calculation_settings.${section}.title`)}</a>
                            })
                          }
                        </div>
                      </div>
                    }
                  </li>
                } else {
                  return <li key={i} className="nav-item">
                    <Link to={sub.path} className={`nav-link ${pathname.includes(sub.path) ? 'active' : ''}`} data-testid={`nav-item-${subKey}`}>
                      <i className={`${sub.icon} pr-2`}></i> {editableTranslate(sub.translation_key, sub?.name)}
                    </Link>
                  </li>
                }
              })
            }

            {
              sections.personnel.length > 0 && <li className={`nav-item text-big text-primary font-weight-bold ${sections.service_agreement.length > 0 ? 'pt-3' : ''} pb-1`}>{editableTranslate('global.menu.settings.section.personnel')}</li>
            }
            {
              sections.personnel.map((sub, i) => {
                const subKey = (sub.path || '').split('/').filter(Boolean).pop();
                // if (!isInternalTenant && ['/settings/roles', '/settings/groups'].includes(sub.path)) return null;

                return <li key={i} className="nav-item">
                  <Link to={sub.path} className={`nav-link ${pathname.includes(sub.path) ? 'active' : ''}`} data-testid={`nav-item-${subKey}`}>
                    <i className={`${sub.icon} pr-2`}></i> {editableTranslate(sub.translation_key, sub?.name)}
                  </Link>
                </li>
              })
            }

            {
              sections.others.length > 0 && <li className={`nav-item text-big text-primary font-weight-bold ${sections.service_agreement.length > 0 || sections.personnel.length > 0 ? 'pt-3' : ''} pb-1`}>{editableTranslate('global.menu.settings.section.others')}</li>
            }
            {
              sections.others.map((sub, i) => {
                const subKey = (sub.path || '').split('/').filter(Boolean).pop();
                return <li key={i} className="nav-item">
                  <Link to={sub.path} className={`nav-link ${pathname.includes(sub.path) ? 'active' : ''}`} data-testid={`nav-item-${subKey}`}>
                    <i className={`${sub.icon} pr-2`}></i> {editableTranslate(sub.translation_key, sub?.name)}
                  </Link>
                </li>
              })
            }
          </ul>
        </div>
      </div>}
      <Outlet context={{ activeFieldset, setActiveFieldset, activeCalculationSection, setActiveCalculationSection, helps }} />
    </div>
  );
}
